import React from "react";
import { AlertTriangle } from "lucide-react";

const HERO_IMAGE = "/assets/images/logo-landing.jpg";
const PATTERN = "/assets/images/blob.svg";

const SectionBlock = ({ number, title, children }) => (
  <section className="border-b border-slate-100 pb-8 last:border-b-0 last:pb-0">
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center font-black shrink-0">
        {number}
      </div>

      <div>
        <h3 className="text-2xl font-black text-[#06183f] mb-3">{title}</h3>
        <div className="space-y-4 text-slate-700 leading-8">{children}</div>
      </div>
    </div>
  </section>
);

const DisclaimerPage = () => {
  return (
    <main className="bg-slate-50 overflow-hidden">
      <section
        className="relative pt-36 pb-24 bg-[#06183f] text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 24, 63, 0.88), rgba(6, 24, 63, 0.94)), url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <img
          src={PATTERN}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
        />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#ff6600]" />
            <span className="text-[#ff6600] font-black tracking-[0.2em] text-sm uppercase">
              AKSUBEB Website Disclaimer
            </span>
            <span className="w-10 h-[3px] bg-[#ff6600]" />
          </div>

          <h1
            className="text-5xl md:text-7xl font-black leading-tight"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Website Disclaimer
          </h1>

          <p className="text-white/80 mt-5 max-w-3xl mx-auto leading-relaxed">
            This disclaimer explains the limits of responsibility, use of
            official information, external links, and user obligations on this
            website.
          </p>

          <p className="mt-4 text-[#ff6600] font-bold">Effective Date: May 2026</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="bg-white rounded-[34px] p-6 md:p-10 shadow-xl border border-slate-100">
          <div className="flex flex-col md:flex-row md:items-center gap-5 mb-10 bg-slate-50 rounded-[26px] p-6 border border-slate-100">
            <div className="w-16 h-16 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center">
              <AlertTriangle size={30} />
            </div>

            <div>
              <h2 className="text-3xl font-black text-[#06183f]">
                Akwa Ibom State Universal Basic Education Board
              </h2>
              <p className="text-slate-500 mt-1">Website: subeb.ak.gov.ng</p>
            </div>
          </div>

          <div className="space-y-9">
            <div className="text-slate-700 leading-8 space-y-4">
              <p>
                This Disclaimer applies to the official website of the Akwa Ibom
                State Universal Basic Education Board, AKSUBEB.
              </p>

              <p>By using this website, you accept the terms of this Disclaimer.</p>
            </div>

            <SectionBlock number="01" title="General Information">
              <p>
                The information published on this website is provided for
                general public information, education service support,
                transparency, and official communication.
              </p>

              <p>
                AKSUBEB makes reasonable efforts to ensure that the information
                on this website is accurate, current, and useful. However, the
                Board does not guarantee that all information will always be
                complete, updated, error-free, or suitable for every purpose.
              </p>
            </SectionBlock>

            <SectionBlock number="02" title="No Professional Advice">
              <p>
                Information on this website should not be treated as legal,
                financial, technical, procurement, employment, or professional
                advice.
              </p>

              <p>
                Users should contact AKSUBEB or the appropriate government
                authority for official confirmation where specific guidance,
                approval, interpretation, or verification is required.
              </p>
            </SectionBlock>

            <SectionBlock number="03" title="Accuracy of Content">
              <p>
                AKSUBEB may update, correct, replace, withdraw, or remove any
                content on this website at any time without prior notice.
              </p>

              <p>
                This may include public notices, project information, policy
                updates, school data, teacher data, publications, images,
                downloadable documents, announcements, and other materials.
              </p>

              <p>
                Users are advised to verify important information directly with
                AKSUBEB before making decisions based on website content.
              </p>
            </SectionBlock>

            <SectionBlock number="04" title="External Links">
              <p>
                This website may contain links to government, partner, or
                third-party websites.
              </p>

              <p>
                These links are provided for public information and convenience
                only.
              </p>

              <p>
                AKSUBEB does not control external websites and is not
                responsible for their content, accuracy, security, privacy
                practices, availability, or services.
              </p>

              <p>Users access external websites at their own discretion.</p>
            </SectionBlock>

            <SectionBlock number="05" title="Website Availability">
              <p>
                AKSUBEB will make reasonable efforts to keep this website
                available, secure, and functional.
              </p>

              <p>
                However, the website may be unavailable from time to time due to
                maintenance, technical faults, internet disruptions, hosting
                issues, security reviews, service upgrades, or circumstances
                beyond the control of AKSUBEB.
              </p>

              <p>
                AKSUBEB will not be liable for any loss, delay, inconvenience,
                or damage arising from temporary or prolonged website
                unavailability.
              </p>
            </SectionBlock>

            <SectionBlock number="06" title="Limitation of Liability">
              <p>
                AKSUBEB will not be liable for any direct or indirect loss,
                damage, claim, cost, delay, inconvenience, or error arising from:
              </p>

              <ul className="list-disc pl-6 space-y-2">
                <li>Use of this website.</li>
                <li>Reliance on information published on this website.</li>
                <li>Inability to access the website.</li>
                <li>Errors, omissions, or outdated information.</li>
                <li>Use of external links.</li>
                <li>
                  Unauthorised access, cyber incidents, or technical failures
                  beyond the reasonable control of AKSUBEB.
                </li>
              </ul>

              <p>
                Users are responsible for how they use information obtained from
                this website.
              </p>
            </SectionBlock>

            <SectionBlock number="07" title="Public Notices and Official Confirmation">
              <p>
                Information published on this website may include public notices,
                announcements, procurement information, recruitment updates,
                project reports, education guidelines, media statements, and
                other official communications.
              </p>

              <p>
                Where a matter requires formal approval, certification,
                appointment, contract award, payment, employment, admission,
                verification, or legal recognition, users should seek written
                confirmation from AKSUBEB or the appropriate authority.
              </p>
            </SectionBlock>

            <SectionBlock number="08" title="User Submissions">
              <p>
                Any information submitted through this website must be accurate,
                lawful, and provided in good faith.
              </p>

              <p>
                AKSUBEB will not be responsible for false, incomplete,
                misleading, or unauthorised information submitted by users,
                schools, contractors, service providers, or third parties.
              </p>

              <p>
                AKSUBEB reserves the right to review, verify, reject, correct, or
                remove submitted information where necessary.
              </p>
            </SectionBlock>

            <SectionBlock number="09" title="Copyright and Use of Materials">
              <p>
                Content on this website, including text, images, documents,
                logos, videos, forms, and design elements, belongs to AKSUBEB or
                is used with proper authorisation.
              </p>

              <p>
                Users may view, download, or print materials for personal,
                educational, research, or official reference purposes.
              </p>

              <p>
                No content from this website may be altered, misrepresented,
                republished, sold, or used for commercial purposes without prior
                written approval from AKSUBEB.
              </p>
            </SectionBlock>

            <SectionBlock number="10" title="Security Disclaimer">
              <p>
                AKSUBEB takes reasonable steps to protect this website and
                related digital services.
              </p>

              <p>
                However, users are responsible for protecting their own devices,
                internet connections, passwords, and personal information when
                using the website.
              </p>

              <p>
                Users should avoid submitting sensitive information through
                unsecured channels.
              </p>
            </SectionBlock>

            <SectionBlock number="11" title="Changes to This Disclaimer">
              <p>
                AKSUBEB may update this Disclaimer from time to time to reflect
                changes in law, operations, technology, public service
                requirements, or website functions.
              </p>

              <p>
                Updated versions will be published on this website with a
                revised effective date.
              </p>
            </SectionBlock>

            <SectionBlock number="12" title="Contact">
              <p>
                For questions, enquiries, complaints, or requests relating to
                this Disclaimer, please contact:
              </p>

              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <p className="font-bold text-[#06183f]">
                  Akwa Ibom State Universal Basic Education Board
                </p>
                <p>Uyo, Akwa Ibom State, Nigeria</p>
                <p>Website: subeb.ak.gov.ng</p>
                <p>Email: info@subeb.ak.gov.ng</p>
              </div>
            </SectionBlock>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DisclaimerPage;